/**
 * AgencyDashboardPage — cross-client rollup for agency operators.
 *
 * Layout:
 *   - Range picker (7d / 30d / 90d) — calls the same GET with ?range=.
 *   - KPI tile strip: clients / clicks / conversions / revenue / payouts / CR / EPC.
 *   - Per-client breakdown table (clicks, conversions, revenue, payouts, CR, EPC).
 *   - CSV export — serialises the in-memory summary and triggers a download.
 *
 * Visuals follow the dash palette: emerald -> sky gradient headline, zinc-200
 * borders, 6-8px rounding, light theme.
 */
import { useState } from "react";
import {
	formatCrPercent,
	formatEpc,
	formatInteger,
	formatMoney,
	toCsv,
	type AgencySummary,
	type RangeKey,
} from "./api";
import { useAgencySummary } from "./hooks";

// ---------------------------------------------------------------------------
// Style tokens — inline for grep-friendliness.
// ---------------------------------------------------------------------------

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const TH =
	"px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900";
const BTN_PRIMARY =
	"inline-flex items-center gap-2 rounded-[6px] bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-zinc-300";
const BTN_RANGE =
	"rounded-[6px] border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 transition hover:border-zinc-300 hover:bg-zinc-50";
const BTN_RANGE_ACTIVE =
	"rounded-[6px] border border-emerald-500 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700 shadow-sm";

const RANGES: { key: RangeKey; label: string }[] = [
	{ key: "7d", label: "7 days" },
	{ key: "30d", label: "30 days" },
	{ key: "90d", label: "90 days" },
];

// ---------------------------------------------------------------------------
// Small pure helpers exported for tests.
// ---------------------------------------------------------------------------

export function csvFilename(range: RangeKey, now: Date = new Date()): string {
	const y = now.getUTCFullYear();
	const m = String(now.getUTCMonth() + 1).padStart(2, "0");
	const d = String(now.getUTCDate()).padStart(2, "0");
	return `agency-dashboard-${range}-${y}${m}${d}.csv`;
}

// ---------------------------------------------------------------------------
// Download helper — guarded for SSR / non-browser environments (jsdom-only
// code paths in tests avoid clicking the link).
// ---------------------------------------------------------------------------

function downloadCsv(summary: AgencySummary): void {
	const csv = toCsv(summary);
	if (
		typeof document === "undefined" ||
		typeof URL === "undefined" ||
		typeof Blob === "undefined"
	) {
		return;
	}
	const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = csvFilename(summary.range);
	a.rel = "noopener";
	a.style.display = "none";
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	setTimeout(() => URL.revokeObjectURL(url), 0);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AgencyDashboardPage() {
	const [range, setRange] = useState<RangeKey>("7d");
	const query = useAgencySummary(range);

	const summary = query.data;
	const hasClients = !!summary && summary.clients.length > 0;
	const canExport = !!summary && hasClients;

	return (
		<main
			data-testid="agency-dashboard-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Agency report
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Agency dashboard
				</h1>
				<p className="text-sm text-zinc-600">
					Cross-client performance rollup across every sub-tenant under this
					agency.
				</p>
			</header>

			<section
				className="flex flex-wrap items-center justify-between gap-3"
				aria-label="Controls"
			>
				<div
					className="flex items-center gap-2"
					role="group"
					aria-label="Date range"
					data-testid="agency-dashboard-range-group"
				>
					{RANGES.map((r) => {
						const active = r.key === range;
						return (
							<button
								key={r.key}
								type="button"
								aria-pressed={active}
								data-testid={`agency-dashboard-range-${r.key}`}
								className={active ? BTN_RANGE_ACTIVE : BTN_RANGE}
								onClick={() => setRange(r.key)}
							>
								{r.label}
							</button>
						);
					})}
				</div>
				<button
					type="button"
					className={BTN_PRIMARY}
					data-testid="agency-dashboard-export"
					disabled={!canExport}
					onClick={() => summary && downloadCsv(summary)}
				>
					Export CSV
				</button>
			</section>

			<section aria-label="KPIs">
				{query.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="agency-dashboard-loading"
					>
						Loading.
					</p>
				) : query.error ? (
					<div className={CARD} role="alert">
						<p className="text-sm text-red-600">{query.error.message}</p>
					</div>
				) : summary ? (
					<KpiStrip summary={summary} />
				) : null}
			</section>

			<section className={CARD} aria-label="Per-client breakdown">
				<div className="mb-3 flex items-center justify-between">
					<h2 className="text-sm font-semibold text-zinc-900">
						Clients in scope
					</h2>
					{summary ? (
						<span className="text-xs text-zinc-500">
							{formatInteger(summary.totals.clients)}{" "}
							{summary.totals.clients === 1 ? "client" : "clients"}
						</span>
					) : null}
				</div>

				{query.isLoading ? (
					<p className="text-sm text-zinc-500">Loading clients.</p>
				) : !summary ? null : (
					<ClientsTable summary={summary} />
				)}
			</section>
		</main>
	);
}

// ---------------------------------------------------------------------------
// KPI tiles — compact grid. Uses the pre-rolled totals from the API.
// Exported for tests so a static render can assert tile values w/o a DOM lib.
// ---------------------------------------------------------------------------

export function KpiStrip({ summary }: { summary: AgencySummary }) {
	const t = summary.totals;
	const tiles: { key: string; label: string; value: string }[] = [
		{ key: "clients", label: "Clients", value: formatInteger(t.clients) },
		{ key: "clicks", label: "Clicks", value: formatInteger(t.clicks) },
		{
			key: "conversions",
			label: "Conversions",
			value: formatInteger(t.conversions),
		},
		{ key: "revenue", label: "Revenue", value: formatMoney(t.revenue) },
		{ key: "payouts", label: "Payouts", value: formatMoney(t.payouts) },
		{ key: "cr", label: "CR", value: formatCrPercent(t.cr) },
		{ key: "epc", label: "EPC", value: formatEpc(t.epc) },
	];
	return (
		<div
			className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7"
			data-testid="agency-dashboard-kpis"
		>
			{tiles.map((tile) => (
				<div
					key={tile.key}
					className="rounded-[8px] border border-zinc-200 bg-white p-4 shadow-sm"
					data-testid={`agency-dashboard-kpi-${tile.key}`}
				>
					<p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
						{tile.label}
					</p>
					<p className="mt-1 text-xl font-semibold tabular-nums text-zinc-900">
						{tile.value}
					</p>
				</div>
			))}
		</div>
	);
}

// ---------------------------------------------------------------------------
// ClientsTable — pure presentation, exported for tests.
// ---------------------------------------------------------------------------

export function ClientsTable({
	summary,
}: {
	summary: AgencySummary;
}) {
	if (summary.clients.length === 0) {
		return (
			<p
				className="text-sm text-zinc-500"
				data-testid="agency-dashboard-empty"
			>
				No sub-tenants report activity in this range.
			</p>
		);
	}
	return (
		<div className="overflow-x-auto">
			<table
				className="w-full border-collapse"
				data-testid="agency-dashboard-table"
			>
				<thead className="bg-zinc-50">
					<tr>
						<th className={TH}>Client</th>
						<th className={`${TH} text-right`}>Clicks</th>
						<th className={`${TH} text-right`}>Conversions</th>
						<th className={`${TH} text-right`}>Revenue</th>
						<th className={`${TH} text-right`}>Payouts</th>
						<th className={`${TH} text-right`}>CR</th>
						<th className={`${TH} text-right`}>EPC</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-zinc-200">
					{summary.clients.map((c) => (
						<tr
							key={c.tenant_id}
							data-testid={`agency-dashboard-row-${c.tenant_id}`}
						>
							<td className={TD}>
								<div className="flex flex-col">
									<span className="font-medium text-zinc-900">{c.name}</span>
									<span className="text-xs text-zinc-500">{c.tenant_id}</span>
								</div>
							</td>
							<td className={`${TD} text-right tabular-nums`}>
								{formatInteger(c.clicks)}
							</td>
							<td className={`${TD} text-right tabular-nums`}>
								{formatInteger(c.conversions)}
							</td>
							<td className={`${TD} text-right tabular-nums`}>
								{formatMoney(c.revenue)}
							</td>
							<td className={`${TD} text-right tabular-nums`}>
								{formatMoney(c.payouts)}
							</td>
							<td className={`${TD} text-right tabular-nums`}>
								{formatCrPercent(c.cr)}
							</td>
							<td className={`${TD} text-right tabular-nums`}>
								{formatEpc(c.epc)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export default AgencyDashboardPage;
